/**
 * GNSS Job Database Service
 * 
 * Manages persistence of GNSS processing jobs and results
 * Provides storage, retrieval, and caching capabilities
 */

import { GNSSProcessingConfig, GNSSJobStatus, GNSSProcessingResult } from '../types';

export interface StoredGNSSJob {
  jobId: string;
  filename: string;
  uploadedAt: string;
  config: GNSSProcessingConfig;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  currentStep: string;
  startedAt?: string;
  completedAt?: string;
  error?: string;
  result?: GNSSProcessingResult;
  processingDuration?: number;
  fileSize: number;
}

export interface GNSSJobQuery {
  status?: string;
  limit?: number;
  offset?: number;
  sortBy?: 'date' | 'status' | 'duration';
  sortOrder?: 'asc' | 'desc';
}

/**
 * IndexedDB wrapper for GNSS job persistence
 */
class GNSSJobDatabase {
  private dbName = 'landsurv_gnss_db';
  private storeName = 'jobs';
  private db: IDBDatabase | null = null;

  /**
   * Initialize database connection
   */
  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, 1);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        // Create jobs object store
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, { keyPath: 'jobId' });
          
          // Create indexes for querying
          store.createIndex('status', 'status', { unique: false });
          store.createIndex('uploadedAt', 'uploadedAt', { unique: false });
          store.createIndex('filename', 'filename', { unique: false });
          store.createIndex('completedAt', 'completedAt', { unique: false });
        }
      };
    });
  }

  /**
   * Store a GNSS job
   */
  async storeJob(job: StoredGNSSJob): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.put(job);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  /**
   * Retrieve a single job by ID
   */
  async getJob(jobId: string): Promise<StoredGNSSJob | null> {
    if (!this.db) throw new Error('Database not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.get(jobId);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result || null);
    });
  }

  /**
   * Query jobs with filters and sorting
   */
  async queryJobs(query: GNSSJobQuery = {}): Promise<StoredGNSSJob[]> {
    if (!this.db) throw new Error('Database not initialized');

    const {
      status,
      limit = 50,
      offset = 0,
      sortBy = 'uploadedAt',
      sortOrder = 'desc',
    } = query;

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);

      let request;
      if (status) {
        // Query by status index
        const index = store.index('status');
        request = index.getAll(status);
      } else {
        // Get all jobs
        request = store.getAll();
      }

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        let results = request.result as StoredGNSSJob[];

        // Sort results
        results.sort((a, b) => {
          let aVal: any = a[sortBy as keyof StoredGNSSJob];
          let bVal: any = b[sortBy as keyof StoredGNSSJob];

          if (sortBy === 'duration') {
            aVal = a.processingDuration || 0;
            bVal = b.processingDuration || 0;
          }

          if (typeof aVal === 'string') {
            return sortOrder === 'asc'
              ? aVal.localeCompare(bVal)
              : bVal.localeCompare(aVal);
          }

          return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
        });

        // Apply pagination
        results = results.slice(offset, offset + limit);

        resolve(results);
      };
    });
  }

  /**
   * Update job status
   */
  async updateJobStatus(
    jobId: string,
    status: StoredGNSSJob['status'],
    update: Partial<StoredGNSSJob>
  ): Promise<void> {
    const job = await this.getJob(jobId);
    if (!job) throw new Error(`Job ${jobId} not found`);

    const updated: StoredGNSSJob = {
      ...job,
      status,
      ...update,
    };

    await this.storeJob(updated);
  }

  /**
   * Delete a job
   */
  async deleteJob(jobId: string): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.delete(jobId);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  /**
   * Clear all jobs (with optional filter)
   */
  async clearJobs(status?: string): Promise<number> {
    if (!this.db) throw new Error('Database not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);

      let request;
      if (status) {
        const index = store.index('status');
        request = index.openCursor(IDBKeyRange.only(status));
        
        let count = 0;
        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest).result;
          if (cursor) {
            cursor.delete();
            count++;
            cursor.continue();
          } else {
            resolve(count);
          }
        };
      } else {
        request = store.clear();
        request.onsuccess = () => resolve(-1); // -1 indicates all cleared
      }

      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get job statistics
   */
  async getStatistics(): Promise<{
    totalJobs: number;
    completedJobs: number;
    failedJobs: number;
    processingJobs: number;
    averageProcessingTime: number;
    totalDataProcessed: number;
  }> {
    const allJobs = await this.queryJobs({ limit: 1000 });

    const completed = allJobs.filter((j) => j.status === 'completed');
    const failed = allJobs.filter((j) => j.status === 'failed');
    const processing = allJobs.filter((j) => j.status === 'processing');

    const avgTime =
      completed.length > 0
        ? completed.reduce((sum, j) => sum + (j.processingDuration || 0), 0) /
          completed.length
        : 0;

    const totalSize = allJobs.reduce((sum, j) => sum + j.fileSize, 0);

    return {
      totalJobs: allJobs.length,
      completedJobs: completed.length,
      failedJobs: failed.length,
      processingJobs: processing.length,
      averageProcessingTime: avgTime,
      totalDataProcessed: totalSize,
    };
  }

  /**
   * Export jobs to JSON
   */
  async exportJobs(status?: string): Promise<string> {
    const jobs = await this.queryJobs({ status, limit: 10000 });
    return JSON.stringify(jobs, null, 2);
  }

  /**
   * Import jobs from JSON
   */
  async importJobs(jsonData: string): Promise<number> {
    const jobs = JSON.parse(jsonData) as StoredGNSSJob[];
    let count = 0;

    for (const job of jobs) {
      try {
        await this.storeJob(job);
        count++;
      } catch (err) {
        console.error(`Failed to import job ${job.jobId}:`, err);
      }
    }

    return count;
  }
}

// Singleton instance
let dbInstance: GNSSJobDatabase | null = null;

/**
 * Get or create database instance
 */
export async function getGNSSJobDatabase(): Promise<GNSSJobDatabase> {
  if (!dbInstance) {
    dbInstance = new GNSSJobDatabase();
    await dbInstance.init();
  }
  return dbInstance;
}

export default GNSSJobDatabase;
